import moment from "moment-timezone";
import { date } from "react-widgets/lib/util/localizers";
import { NameWithHover, SelfLabelBadge } from "styledComponents/common";

export const getHours = time => {
  if (!time) return "-"; // handle null/undefined

  const times = time.split(":");
  const hours = parseInt(times[0], 10);
  const minutes = parseInt(times[1], 10);
  const seconds = times[2] ? parseInt(times[2], 10) : 0;

  if (hours > 0 && minutes > 0) return `${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h`;
  if (minutes > 0) return `${minutes}m`;
  if (seconds > 0) return `${seconds}s`; // only shows when hours & minutes are 0
  return "-";
};

export const formatHmsString = timeString => {
  if (!timeString || timeString === "-") {
    return "-";
  }

  const hasHours = timeString.includes("h");
  const hasMinutes = timeString.includes("m");
  const hasSeconds = timeString.includes("s");

  const parts = timeString.split(" ");
  const hoursPart = hasHours ? parseInt(parts.find(p => p.includes("h"))) : 0;
  const minutesPart = hasMinutes
    ? parseInt(parts.find(p => p.includes("m")))
    : 0;
  const secondsPart = hasSeconds
    ? parseInt(parts.find(p => p.includes("s")))
    : 0;

  let result = [];
  if (hoursPart > 0) result.push(`${hoursPart}h`);
  if (minutesPart > 0) result.push(`${minutesPart}m`);
  if (secondsPart > 0) result.push(`${secondsPart}s`);

  // if all are 0, just return "0s" (or "-" if you prefer)
  return result.length > 0 ? result.join(" ") : "0s";
};

export const getHoursWithSeconds = time => {
  if (!time) return "-"; // handle null/undefined

  const times = time.split(":");
  const hours = parseInt(times[0], 10) || 0;
  const minutes = parseInt(times[1], 10) || 0;
  const seconds = times[2] ? parseInt(times[2], 10) : 0;

  // Build result string dynamically
  let result = "";
  if (hours > 0) result += `${hours}h `;
  if (minutes > 0) result += `${minutes}m `;
  if (seconds > 0) result += `${seconds}s`;

  return result.trim() || "-"; // fallback when everything is 0
};

export const getHoursWithoutZero = time => {
  const times = time.split(":");
  const hours = parseInt(times[0]);
  const minutes = parseInt(times[1]);
  if (!hours) {
    return `${minutes}m`;
  }
  if (!minutes) {
    return `${hours}h`;
  }
  return `${hours}h ${minutes}m`;
};

export const getTotal = (prev, next) => {
  const splittedPrev = prev.split(":");
  const splittedNext = next.split(":");

  const prevHour = parseInt(splittedPrev[0]);
  const prevMin = parseInt(splittedPrev[1]);
  const prevSec = parseInt(splittedPrev[2]);

  const nextHour = parseInt(splittedNext[0]);
  const nextMin = parseInt(splittedNext[1]);
  const nextSec = parseInt(splittedNext[2]);

  let totalHour = prevHour + nextHour;
  let totalMin = prevMin + nextMin;
  let totalSec = prevSec + nextSec;

  if (totalSec > 60) {
    totalMin += parseInt(totalSec / 60);
    totalSec = parseInt(totalSec % 60);
  }
  if (totalMin >= 60) {
    totalHour += parseInt(totalMin / 60);
    totalMin = parseInt(totalMin % 60);
  }
  return `${totalHour}:${totalMin}:${totalSec}`;
};

export const toHHMMSS = secs => {
  const getSeconds = `0${secs % 60}`.slice(-2);
  const minutes = `${Math.floor(secs / 60)}`;
  const getMinutes = `0${minutes % 60}`.slice(-2);
  const getHours =
    secs < 36000
      ? `0${Math.floor(secs / 3600)}`.slice(-2)
      : `${Math.floor(secs / 3600)}`;

  return `${getHours}:${getMinutes}:${getSeconds}`;
};

export const getHourMin = seconds => {
  let hourMin = ` ${parseInt(seconds / 3600)} h ${parseInt(
    (seconds % 3600) / 60,
  )} m`;
  return hourMin;
};

export const toHHMM = secs => {
  const minutes = `${Math.floor(secs / 60)}`;
  const getMinutes = `0${minutes % 60}`.slice(-2);
  const getHours = `0${Math.floor(secs / 3600)}`.slice(-2);

  return `${getHours}:${getMinutes}`;
};

export const formatDurationToHoursMinutes = duration => {
  let formattedDuration = "";
  let hours = parseInt(duration / 3600);
  let minutes = Math.round((duration / 60) % 60);
  if (duration <= 0) {
    duration = 0;
  }
  if (duration < 60) {
    formattedDuration = `${parseInt(duration)}s`;
  } else {
    if (hours === 0) {
      hours = ``;
    } else {
      hours = `${hours}h`;
    }

    minutes = `${minutes}m`;

    formattedDuration = `${hours} ${minutes}`;
  }
  return formattedDuration;
};

export const validateEmail = email => {
  // eslint-disable-next-line
  let emailRegex =
    /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return emailRegex.test(email);
};

// Validate password
export function password_validate(p) {
  return /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9]).{8,}$/.test(p);
}

export const getYouTubeVideoId = (url) => {
  if (!url || typeof url !== "string") return null;
  const regex = /(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
  const match = url.match(regex);
  return match ? match[1] : null;
};

export const isYouTubeUrl = (url) => {
  return !!getYouTubeVideoId(url);
};

function isASCII(str) {
  // eslint-disable-next-line
  return /^[\x00-\x7F]*$/.test(str);
}

export function isValidPhone(phone) {
  const regex = /^(?:[0-9] ?){6,14}[0-9]$/;
  return regex.test(phone);
}

export function checkUrl(url) {
  const regex = /https?:\/\//gi;
  return regex.test(url);
}

export function checkHtml(value) {
  const regex = /<\/?[a-z][^>]*>/gi;
  return regex.test(value);
}

export function handleValidation(name, type, value, options) {
  let res = { isValid: true, fieldName: name, message: "" };
  if (options && options.required && !value) {
    res.message = "Field is required!";
    res.isValid = false;
    res.required = options.required;
  } else if (
    options &&
    options.required &&
    typeof value !== "undefined" &&
    value.length === 0
  ) {
    res.message = "Field is required!";
    res.isValid = false;
    res.required = options.required;
  } else if (
    name !== "email" &&
    options &&
    options.required &&
    typeof value === "string" &&
    value.toLowerCase().includes("select")
  ) {
    res.message = "Choose Valid Option!";
    res.isValid = false;
    res.required = options.required;
  } else if (
    options &&
    options.required &&
    options.multiSelect &&
    value.length === 0
  ) {
    res.message = "Choose At Least One Option!";
    res.isValid = false;
    res.required = options.required;
  } else if (options && options.minLength && value.length < options.minLength) {
    res.message = "Minimum length should be " + options.minLength + "!";
    res.isValid = false;
    res.required = options.required;
  } else if (options && options.maxLength && value.length > options.maxLength) {
    res.message = "Maximum length should be " + options.maxLength + "!";
    res.isValid = false;
    res.required = options.required;
  } else if (options && options.isASCII && !isASCII(value)) {
    res.message = "Only ASCII characters are allowed!";
    res.isValid = false;
    res.required = options.required;
  } else if (options && options.phone && !isValidPhone(value)) {
    res.message = "Not valid phone number!";
    res.isValid = false;
  } else if (options && options.contact && !isValidPhone(value)) {
    res.message = "Not valid phone number!";
    res.isValid = false;
    res.required = options.required;
  } else if (options && name === "phone" && !isValidPhone(value)) {
    res.message = "Not valid phone number!";
    res.isValid = false;
    res.required = options.required;
  } else if (options && name === "contact" && !isValidPhone(value)) {
    res.message = "Not valid phone number!";
    res.isValid = false;
    res.required = options.required;
  } else {
    switch (name) {
      case "email":
        if (!value.match(/^([\w.%+-]+)@([\w-]+\.)+([\w]{2,})$/i)) {
          res.message = "Not valid email!";
          res.isValid = false;
          res.required = options.required;
        }
        break;
      case "password":
        if (value.length < 6) {
          res.message = "Min 6 characters!";
          res.isValid = false;
          res.required = options.required;
        }
        break;
      default:
        break;
    }
  }
  return res;
}

const rad = x => {
  return (x * Math.PI) / 180;
};

export const getDistanceFromCoords = (p1, p2) => {
  const R = 6378137; // Earth’s mean radius in meter
  const dLat = rad(p2.lat - p1.lat);
  const dLong = rad(p2.lng - p1.lng);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(rad(p1.lat)) *
      Math.cos(rad(p2.lat)) *
      Math.sin(dLong / 2) *
      Math.sin(dLong / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const d = R * c;
  return d; // returns the distance in meter
};

export const getFullName = user => {
  const { first_name, last_name } = user;

  let fullName = first_name;

  if (last_name) {
    fullName += ` ${last_name}`;
  }

  return fullName;
};

export const arrayInRange = (start, stop, step) => {
  return Array.from(
    { length: (stop - start) / step + 1 },
    (value, index) => start + index * step,
  );
};

export const formatAMPM = (datetime, minuteFlag) => {
  const date = new Date(datetime);
  let hours = date.getHours();
  let minutes = date.getMinutes();
  let ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  hours = hours ? hours : 12;
  minutes = minutes < 10 ? "0" + minutes : minutes;
  let strTime = "";
  if (minuteFlag) {
    strTime = hours + ":" + minutes + " " + ampm;
  } else {
    strTime = hours + ampm;
  }

  return strTime;
};

export const getMapCircle = (lat, lng, radius, detail = 8) => {
  const r = 6371;
  const pi = Math.PI;
  const _lat = (lat * pi) / 180;
  const _lng = (lng * pi) / 180;
  const d = radius / 1000 / r;

  let result = "";

  for (let i = 0; i <= 360; i += detail) {
    const brng = (i * pi) / 180;

    let pLat = Math.asin(
      Math.sin(_lat) * Math.cos(d) +
        Math.cos(_lat) * Math.sin(d) * Math.cos(brng),
    );
    const pLng =
      ((_lng +
        Math.atan2(
          Math.sin(brng) * Math.sin(d) * Math.cos(_lat),
          Math.cos(d) - Math.sin(_lat) * Math.sin(pLat),
        )) *
        180) /
      pi;
    pLat = (pLat * 180) / pi;

    result += "|" + pLat + "," + pLng;
  }
  return encodeURI(result);
};

export const getTwoLetter = text => {
  if (!text) return "";

  const words = text.trim().split(" ");
  const firstLetter = words[0]?.charAt(0).toUpperCase() || "";
  const secondLetter =
    words[1]?.charAt(0).toUpperCase() ||
    (words[0]?.length > 1 ? words[0].charAt(1).toUpperCase() : "");

  return `${firstLetter}${secondLetter}`;
};

export const globalDateFormat = (
  date,
  timezone,
  fallbackFormat = "DD-MM-YYYY",
) => {
  const globalFormat = localStorage.getItem("date_format") || fallbackFormat;
  const orgTimezone = timezone || "UTC";
  // If date is a unix timestamp
  if (typeof date == "number") {
    const unixDate = moment.unix(date).tz(orgTimezone).format(globalFormat);
    return unixDate;
  }
  return moment(date + "Z")
    .tz(orgTimezone)
    .format(globalFormat);
};

export const globalDateTimeFormat = (datetime, format) => {
  const fallbackDate = "DD-MM-YYYY";
  const dateFormat = localStorage.getItem("date_format") || fallbackDate;
  const timeFormat = localStorage.getItem("time_format") || "h:mm a";

  if (format == "daydate") {
    // If datetime is a unix timestamp
    if (typeof datetime === "number") {
      return moment.unix(datetime).format(`ddd ${dateFormat}`);
    }
    return moment(datetime).format(`ddd ${dateFormat}`);
  }

  if (format == "datetime") {
    // If datetime is a unix timestamp
    if (typeof datetime === "number") {
      return moment.unix(datetime).format(`${dateFormat} ${timeFormat}`);
    }
  }
  return moment(datetime).format(`${dateFormat} ${timeFormat}`);
};

/**
 * Formats a time string in the specified timezone
 * @param {string} timeString - The time string to format (ISO format)
 * @param {string} timezone - The timezone to convert to (e.g., "America/New_York", "Asia/Dhaka")
 * @param {string} format - The moment.js format string (default: "h:mm a")
 * @returns {string} - Formatted time string or empty string if timeString is invalid
 */
export const formatTimeInTimezone = (
  timeString,
  timezone,
  format = "h:mm a",
) => {
  if (!timeString) return "";

  // Check if time has timezone info (contains + or - followed by digits)
  const hasTimezone = /[+-]\d{2}:?\d{2}$/.test(timeString);
  let timeMoment;

  if (hasTimezone) {
    // Time already has timezone, parse as-is
    timeMoment = moment(timeString);
  } else {
    // Time doesn't have timezone, assume UTC
    timeMoment = moment.utc(timeString);
  }

  // Convert to specified timezone and format
  const targetTimezone = timezone || moment.tz.guess();
  return timeMoment.tz(targetTimezone).format(format);
};

export function showSelfLabel(
  userId,
  userName,
  selfLabel = "me",
  hoverUnderline = false,
) {
  const currentUserId = localStorage.getItem("user_id");

  if (!currentUserId) {
    return (
      <NameWithHover hoverUnderline={hoverUnderline}>{userName}</NameWithHover>
    );
  }
  if (userId === currentUserId) {
    return (
      <>
        <NameWithHover hoverUnderline={hoverUnderline}>
          {userName}
        </NameWithHover>
        <SelfLabelBadge>{selfLabel}</SelfLabelBadge>
      </>
    );
  }

  return (
    <NameWithHover hoverUnderline={hoverUnderline}>{userName}</NameWithHover>
  );
}

export function putCurrentUserFirst(members) {
  const currentUserId = localStorage.getItem("user_id");
  if (!currentUserId || !members || !Array.isArray(members))
    return members || [];

  const others = members.filter(m => m.id !== currentUserId);
  const me = members.find(m => m.id === currentUserId);

  return me ? [me, ...others] : members;
}

export function generateEventUniqueId() {
  const timestamp = Date.now().toString().slice(-6);
  const random = Math.floor(Math.random() * 1000000)
    .toString()
    .padStart(6, "0");
  return timestamp + random;
}

export function normalizeData(value) {
  if (!value) return "";
  return value.trim().toLowerCase();
}

export async function hashForFacebook(value) {
  if (!value) return "";
  const msgBuffer = new TextEncoder().encode(value);
  const hashBuffer = await crypto.subtle.digest("SHA-256", msgBuffer);
  return Array.from(new Uint8Array(hashBuffer))
    .map(b => b.toString(16).padStart(2, "0"))
    .join("");
}

// Get detailed password validation message
export function getPasswordValidationMessage(password) {
  if (!password) return "Password is required";

  const requirements = [];

  if (password.length < 8) {
    requirements.push("at least 8 characters");
  }

  if (!/[A-Za-z]/.test(password)) {
    requirements.push("one letter");
  }

  if (!/[0-9]/.test(password)) {
    requirements.push("one number");
  }

  if (requirements.length === 0) {
    return null; // Password is valid
  }

  if (requirements.length === 1) {
    return `Password must contain ${requirements[0]}`;
  }

  const lastRequirement = requirements.pop();
  return `Password must contain ${requirements.join(
    ", ",
  )} and ${lastRequirement}`;
}

//wrtie to function that return  current month and currretn year and last month and last year in format like this {current_month: "September", current_year: 2023, last_month: "August", last_year: 2022}
export function getCurrentAndLastMonthYear() {
  const now = moment();

  const current_month = now.format("MMMM");
  const current_year = now.year();

  const lastMonthDate = now.clone().subtract(1, "month");
  const last_month = lastMonthDate.format("MMMM");

  const last_year = now.clone().subtract(1, "year").year();

  return { current_month, current_year, last_month, last_year };
}

export function formatCompletionTime(value, timezone) {
  if (!value) return "-";

  const targetTimezone = timezone || moment.tz.guess();
  const completionMoment = moment.utc(value).tz(targetTimezone);
  const nowInTimezone = moment.tz(targetTimezone);

  if (completionMoment.isSame(nowInTimezone, "day")) {
    return `Today, ${completionMoment.format("h:mm a")}`;
  }

  if (completionMoment.isSame(nowInTimezone.clone().subtract(1, "day"), "day")) {
    return `Yesterday, ${completionMoment.format("h:mm a")}`;
  }

  return completionMoment.format("D MMM, h:mm a");
}

export function formatDelta(value) {
  if (value === null || value === undefined) return null;
  const numberValue = Number(value);
  if (!Number.isFinite(numberValue)) return null;
  const sign = numberValue >= 0 ? "+" : "";
  return `${sign}${Math.abs(numberValue).toFixed(1)}%`;
}

export function getDeltaType(percentageChange) {
  if (percentageChange === null || percentageChange === undefined) return null;
  return percentageChange >= 0 ? "increase" : "decrease";
}
